import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { COLORS } from '../../../IndividualPlan/tokens';
import type { PeTrajectoryPoint, ScoreUnit } from '../../types';
import { ScaleResetMarker } from './ScaleResetMarker';
import { WidaCurrentScalePanel } from './WidaCurrentScalePanel';
import { WidaTrajectoryProficiencyChart } from './WidaTrajectoryProficiencyChart';
import { buildWidaTrajectory, type ChartPoint } from './widaTrajectoryData';
import { formatSchoolYearShort } from './YearAxisTick';

type ChartVariant = 'default' | 'perDomain';

type WidaTrajectoryChartProps = {
  trajectory: PeTrajectoryPoint[];
  unit: ScoreUnit;
  threshold?: number | null;
  height?: number;
  fontSize?: number;
  variant?: ChartVariant;
  accentColor?: string;
};

const SCALE_MIN = 100;
const SCALE_MAX = 600;
const SCALE_TICKS = [100, 200, 300, 400, 500, 600];

const PanelHeader = styled(Typography)({
  color: COLORS.muted,
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 4,
  textAlign: 'center',
});

type ScaleScoreLineProps = {
  points: ChartPoint[];
  height?: number;
  width?: number;
  fontSize?: number;
  variant?: ChartVariant;
  accentColor?: string;
};

function WidaScaleScoreLine({
  points,
  height = 260,
  width = 360,
  fontSize = 16,
  variant = 'default',
  accentColor = COLORS.blue,
}: ScaleScoreLineProps) {
  const perDomain = variant === 'perDomain';
  const left = perDomain ? 34 : 72;
  const right = perDomain ? 34 : 16;
  const top = perDomain ? 34 : 24;
  const bottom = perDomain ? 34 : 40;
  const innerW = width - left - right;
  const innerH = height - top - bottom;

  const slotCount = points.length;
  const x = (index: number) => {
    if (slotCount <= 1) return left + innerW / 2;
    return left + (index / (slotCount - 1)) * innerW;
  };
  const y = (value: number) => top + (1 - (value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * innerH;
  const polyline = points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
  const dotRadius = perDomain ? 7 : 6;
  const valueGap = dotRadius + fontSize * 0.6;
  const lineColor = perDomain ? accentColor : COLORS.navy;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ height: 'auto', maxHeight: height, display: 'block' }}
      role="img"
      aria-label="WIDA scale-score trajectory"
    >
      {!perDomain &&
        SCALE_TICKS.map((t) => (
          <g key={`tick-${t}`}>
            <line x1={left} y1={y(t)} x2={width - right} y2={y(t)} stroke={COLORS.hairlineSoft} />
            <text
              x={left - 8}
              y={y(t) + 5}
              textAnchor="end"
              fill={COLORS.muted}
              fontSize={fontSize}
              fontWeight={500}
            >
              {t}
            </text>
          </g>
        ))}

      {perDomain && (
        <line x1={0} y1={top + innerH} x2={width} y2={top + innerH} stroke={COLORS.hairline} />
      )}

      {points.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          stroke={lineColor}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {points.map((p, i) => (
        <circle
          key={`pt-${p.schoolYear}`}
          cx={x(i)}
          cy={y(p.value)}
          r={dotRadius}
          fill="#FFFFFF"
          stroke={lineColor}
          strokeWidth={2}
        />
      ))}

      {perDomain &&
        points.map((p, i) => (
          <text
            key={`value-${p.schoolYear}`}
            x={x(i)}
            y={y(p.value) - valueGap}
            textAnchor="middle"
            fill={COLORS.muted}
            fontSize={fontSize}
            fontWeight={500}
          >
            {Math.round(p.value)}
          </text>
        ))}

      {points.map((p, i) => (
        <text
          key={`xlabel-${p.schoolYear}`}
          x={x(i)}
          y={height - 12}
          textAnchor="middle"
          fill={COLORS.muted}
          fontSize={fontSize}
          fontWeight={600}
        >
          {formatSchoolYearShort(p.schoolYear)}
        </text>
      ))}
    </svg>
  );
}

type PriorScalePanelProps = {
  points: ChartPoint[];
  unit: ScoreUnit;
  threshold?: number | null;
  height?: number;
  fontSize?: number;
  variant?: ChartVariant;
  accentColor?: string;
};

function PriorScalePanel({
  points,
  unit,
  threshold,
  height,
  fontSize,
  variant,
  accentColor,
}: PriorScalePanelProps) {
  if (unit === 'proficiency_level') {
    return (
      <WidaTrajectoryProficiencyChart
        points={points}
        threshold={threshold}
        height={height}
        fontSize={fontSize}
        variant={variant}
        accentColor={accentColor}
      />
    );
  }
  return (
    <WidaScaleScoreLine
      points={points}
      height={height}
      fontSize={fontSize}
      variant={variant}
      accentColor={accentColor}
    />
  );
}

export function WidaTrajectoryChart(props: WidaTrajectoryChartProps) {
  if (props.variant === 'perDomain') {
    return <TrajectoryChartBody {...props} />;
  }
  return (
    <Box data-testid="wida-trajectory-chart">
      <TrajectoryChartBody {...props} />
    </Box>
  );
}

function TrajectoryChartBody({
  trajectory,
  unit,
  threshold,
  height = 260,
  fontSize = 16,
  variant = 'default',
  accentColor = COLORS.blue,
}: WidaTrajectoryChartProps) {
  const perDomain = variant === 'perDomain';
  const { priorScalePoints, currentScalePoints, spansReset } = buildWidaTrajectory(
    trajectory,
    unit,
  );
  const currentDomain: [number, number] = unit === 'scale_score' ? [SCALE_MIN, SCALE_MAX] : [1, 6];
  const currentTicks = unit === 'scale_score' ? SCALE_TICKS : [1, 2, 3, 4, 5, 6];
  const priorThreshold = unit === 'proficiency_level' ? threshold : null;

  if (spansReset) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
          <Box data-testid="prior-scale-panel" sx={{ flex: 1, minWidth: 0 }}>
            {!perDomain && <PanelHeader>Pre-2025-26 WIDA Scale</PanelHeader>}
            <PriorScalePanel
              points={priorScalePoints}
              unit={unit}
              threshold={priorThreshold}
              height={height}
              fontSize={fontSize}
              variant={variant}
              accentColor={accentColor}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
            {!perDomain && (
              <PanelHeader aria-hidden sx={{ visibility: 'hidden' }}>
                &nbsp;
              </PanelHeader>
            )}
            <Box sx={{ flex: 1, display: 'flex' }}>
              <ScaleResetMarker tone={perDomain ? 'light' : 'dark'} />
            </Box>
          </Box>
          <Box data-testid="current-scale-panel" sx={{ flex: 1, minWidth: 0 }}>
            {!perDomain && (
              <PanelHeader sx={{ color: COLORS.navy }}>Current WIDA Scale</PanelHeader>
            )}
            <WidaCurrentScalePanel
              points={currentScalePoints}
              yDomain={currentDomain}
              yTicks={currentTicks}
              height={height}
              fontSize={fontSize}
              variant={variant}
              unit={unit}
              accentColor={accentColor}
            />
          </Box>
        </Box>
        {!perDomain && (
          <Typography
            component="div"
            data-testid="wida-scale-reset-callout"
            sx={{
              textAlign: 'center',
              color: COLORS.faint,
              fontSize: 12,
              fontWeight: 600,
              mt: 0.5,
            }}
          >
            WIDA scale reset
          </Typography>
        )}
      </Box>
    );
  }

  if (priorScalePoints.length > 0) {
    return (
      <Box data-testid="prior-scale-panel">
        <PriorScalePanel
          points={priorScalePoints}
          unit={unit}
          threshold={priorThreshold}
          height={height}
          fontSize={fontSize}
          variant={variant}
          accentColor={accentColor}
        />
      </Box>
    );
  }

  if (currentScalePoints.length > 0) {
    return (
      <Box data-testid="current-scale-panel">
        <WidaCurrentScalePanel
          points={currentScalePoints}
          yDomain={currentDomain}
          yTicks={currentTicks}
          height={height}
          fontSize={fontSize}
          variant={variant}
          unit={unit}
          accentColor={accentColor}
        />
      </Box>
    );
  }

  return null;
}
